import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, token, logout } = useAuth();
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenu, setMobileMenu] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isLoggedIn = !!token && !!user;

  return (
    <header className={`fixed top-0 w-full z-50 transition-all duration-300 ${scrolled ? 'bg-white/95 backdrop-blur-lg shadow-md' : 'bg-white/80 backdrop-blur-sm'}`} data-testid="main-header">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group" data-testid="logo-link">
            <img src="/logo.png" alt="HumanOnRent.AI" className="w-11 h-11 rounded-xl shadow-lg group-hover:shadow-orange-500/40 transition-all" />
            <div>
              <span className="font-bold text-xl bg-gradient-to-r from-orange-600 via-pink-600 to-purple-600 bg-clip-text text-transparent">
                HumanOnRent.AI
              </span>
              <p className="text-[10px] text-gray-500 -mt-1">India's First AI-Driven Platform</p>
            </div>
          </Link>

          {/* Center Tabs - Rent a Human / Rent Yourself */}
          <div className="hidden md:flex items-center gap-1 bg-gray-100 rounded-full p-1">
            <button
              onClick={() => isLoggedIn ? navigate('/search') : navigate('/auth')}
              className="px-5 py-2 rounded-full text-sm font-semibold transition-all bg-gradient-to-r from-orange-500 to-pink-500 text-white shadow-md"
              data-testid="tab-rent-human"
            >
              Rent a Human
            </button>
            <button
              onClick={() => isLoggedIn ? navigate('/dashboard') : navigate('/auth')}
              className="px-5 py-2 rounded-full text-sm font-semibold transition-all bg-white text-gray-700 hover:text-orange-600 shadow-sm"
              data-testid="tab-rent-yourself"
            >
              Rent Yourself
            </button>
          </div>

          {/* Right Side */}
          <div className="flex items-center gap-3">
            {isLoggedIn ? (
              <div className="flex items-center gap-3">
                <Button
                  onClick={() => navigate('/dashboard')}
                  variant="outline"
                  className="rounded-full border-gray-200 text-sm hidden sm:flex"
                  data-testid="dashboard-btn"
                >
                  Dashboard
                </Button>
                <Button
                  onClick={logout}
                  variant="ghost"
                  className="text-sm text-gray-500"
                  data-testid="logout-btn"
                >
                  Sign Out
                </Button>
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <button
                  onClick={() => navigate('/auth')}
                  className="text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors hidden sm:block"
                  data-testid="signin-btn"
                >
                  Sign In
                </button>
                <Button
                  onClick={() => navigate('/auth')}
                  className="btn-gradient rounded-full px-6 text-sm"
                  data-testid="get-started-btn"
                >
                  Get Started
                </Button>
              </div>
            )}

            {/* Mobile Menu Toggle */}
            <button
              onClick={() => setMobileMenu(!mobileMenu)}
              className="md:hidden p-2 hover:bg-gray-100 rounded-xl"
              data-testid="mobile-menu-toggle"
            >
              {mobileMenu ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenu && (
          <div className="md:hidden py-4 border-t border-gray-100 space-y-2 animate-fade-in-up">
            <button onClick={() => { isLoggedIn ? navigate('/search') : navigate('/auth'); setMobileMenu(false); }} className="w-full text-left py-3 px-4 rounded-xl bg-gradient-to-r from-orange-500 to-pink-500 text-white font-semibold">
              Rent a Human
            </button>
            <button onClick={() => { isLoggedIn ? navigate('/dashboard') : navigate('/auth'); setMobileMenu(false); }} className="w-full text-left py-3 px-4 rounded-xl bg-gray-100 text-gray-700 font-semibold">
              Rent Yourself & Earn
            </button>
            {isLoggedIn && (
              <button onClick={() => { navigate('/dashboard'); setMobileMenu(false); }} className="w-full text-left py-3 px-4 rounded-xl bg-gray-50 text-gray-700">
                Dashboard
              </button>
            )}
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
